/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import * as React from 'react';
import { Fragment } from 'react';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import { Headline, LayoutItem, Text, TextLink } from '@oneaudi/unified-web-components';
import type { HeadlineVariant } from '@oneaudi/unified-web-components/dist/cjs/components/headline';
import ImprintModal from './ImprintModal';
import type { VariantConfig, VtpDealer } from './PartnerBusinessCard';
import { useTrackingManager } from '../useTrackingManager';
import { PartnerContext } from '../PartnerProvider';
import { messages } from '../../i18n/messages';
import { isTranslationLoaded } from '../../lib/isTranslationLoaded';
import { InvestorSharedVehicle } from './VTPCards/InvestorSharedVehicle';
import { BevAgencyDealer } from './VTPCards/BevAgencyDealer';
import { BevAgencyVehicle } from './VTPCards/BevAgencyVehicle';
import { NationwideSellingVehicle } from './VTPCards/NationwideSellingVehicle';
import { createDirectionsLink } from './PartnerLinks';

const NULL_VTP_CONFIG = {
  isBevAgency: false,
  isBEVAgency: false,
  isBEVAgencyDealer: false,
  investorSharedVehicle: false,
  nationWideSelling: false,
  imprint: '',
  registrationNumber: '',
  dealers: [],
  locale: '',
  vtpDealer: {
    city: '',
    dealerContextLinkData: [
      {
        context: '',
        dealerId: '',
        geoLocation: {
          lat: 0,
          lon: 0,
        },
        groupName: '',
        hasDealerAddressData: false,
        originalGroupName: '',
      },
    ],
    email: '',
    geoLocation: {
      lat: 0,
      lon: 0,
    },
    id: '',
    isAffiliateCompany: false,
    name: '',
    phoneNumbers: [
      {
        number: '',
        type: '',
      },
    ],
    street: '',
    trackingId: '',
    zipCode: '',
  },
  dynamicLabel: '',
};

interface Props {
  showTitle: boolean;
}

const StyledHeadlineAsLink = styled.div`
  > a {
    border-bottom: 0;
    color: ${process.env.IS_LEGACY ? '#333' : '#fff'};
    &:hover {
      color: ${process.env.IS_LEGACY ? '#4d4d4d' : 'hsla(216, 33%, 99%, 0.7)'};
    }

    text-decoration: none;
  }
`;

const StyledTextLink = styled(TextLink)`
  text-decoration: none;
`;

const AddressContainer = styled(LayoutItem)<Props>`
  display: grid;

  .pbc-partner-name {
    font-weight: ${(props) => (props.showTitle ? 'normal' : 'normal')};
    margin: 0 0 24px;
  }

  .investor-shared {
    margin: 0 0 16px 0;
  }

  dialog {
    border: none;
    display: block;
    inset: 0;
    margin: 0 auto;
    max-height: 100%;
    max-width: 1024px;
    overflow: auto;
    padding: 0;
    transition: opacity 0.25s;
  }

  dialog:not([open]) {
    opacity: 0;
    pointer-events: none;
  }

  dialog::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
  dialog:not([open]) {
    background-color: #fff;
  }
  h2,
  h3 {
    margin: 0 0 24px;
  }
`;

const RegNumber = styled(Text)`
  margin: 0 0 24px;
`;

export interface Dealer {
  id: string;
  name: string;
}
const Wrapper = styled.div`
  max-width: 375px;
`;

interface PartnerBusinessCardTagProps {
  variantConfig: VariantConfig;
  unitTest?: boolean;
  vtpData?: {
    imprint?: string;
    registrationNumber?: string;
    dealers: Dealer[];
    isBevAgency?: boolean;
    isBEVAgency?: boolean;
    isBEVAgencyDealer?: boolean;
    investorSharedVehicle: boolean;
    nationWideSelling: boolean;
    vtpDealerResultsURL?: string;
    locale: string;
    vtpDealer: VtpDealer;
    dynamicLabel: string | undefined;
  };
}

export const PartnerBusinessCardTag: React.FC<PartnerBusinessCardTagProps> = ({
  variantConfig,
  vtpData = NULL_VTP_CONFIG,
  unitTest = false,
}): React.ReactElement => {
  const { partner } = React.useContext(PartnerContext);
  const pbcRef = React.useRef<HTMLDialogElement>(null);
  const trackingManager = useTrackingManager();
  const dealerNameLinkAriaLabel = useI18n(messages.dealerNameLinkAriaLabel);
  const [openDialog, setIsOpen] = React.useState(false);

  const { displayOfficialName, isGoogleMapLink, isLinkedToPWS } =
    variantConfig.contactDetails || variantConfig;
  const { isBevAgency, isBEVAgency, isBEVAgencyDealer, investorSharedVehicle, nationWideSelling } =
    vtpData;

  const partnerName = displayOfficialName ? partner.officialName : partner.nonOfficialName;

  const partnerAddressSize = partner.address && partner.address.length;

  const partnerAddress = partnerAddressSize
    ? partner.address.map((item, index) => (
        <Fragment key={index}>{`${item}${index === partnerAddressSize - 1 ? '' : ', '}`}</Fragment>
      ))
    : [];

  const vtpTitle = useI18n(messages.vtpHeadline);
  const showTitle = isTranslationLoaded(messages.vtpHeadline.id, vtpTitle);
  let vtpDealerObj: VtpDealer | null = null;
  let dealerAddress: string | null = null;
  let dealerDirectionUrl: string | null = null;

  if (vtpData && vtpData.vtpDealer) {
    vtpDealerObj = vtpData.vtpDealer;
    dealerAddress =
      vtpDealerObj && `${vtpDealerObj.street}, ${vtpDealerObj.zipCode} ${vtpDealerObj.city}`;
    dealerDirectionUrl = createDirectionsLink({
      ...vtpData.vtpDealer,
      locale: vtpData.locale,
      address: dealerAddress,
    });
  }

  const {
    headline = {
      headlineTag: 'h3',
      headlineOrder: '2',
    },
  } = variantConfig;
  const { headlineTag, headlineOrder } = headline;

  const nameVariant = (() => {
    if (showTitle) return { type: 'headline', order: '4', weight: 'normal' };
    return { type: 'headline', order: headlineOrder, weight: 'normal' };
  })() as HeadlineVariant;

  const nameAs = (() => {
    if (showTitle) return 'div';
    return headlineTag;
  })();

  const className = () => {
    if (investorSharedVehicle) return 'investor-shared';
    return '';
  };

  const registrationNumber = useI18n(messages.registrationNumber);
  const imprintMessage = useI18n(messages.imprintMessage);

  const isDealerInfoIntegrated =
    isBevAgency || isBEVAgency || isBEVAgencyDealer || investorSharedVehicle || nationWideSelling;

  const vehicleCard = (() => {
    if (isBevAgency || isBEVAgency) {
      return <BevAgencyVehicle vtpData={vtpData} pbcRef={pbcRef} />;
    }
    if (isBEVAgencyDealer) {
      return (
        <BevAgencyDealer
          vtpData={vtpData}
          partnerName={partnerName}
          pbcRef={pbcRef}
          isGoogleMapLink
          partnerAddress={partnerAddress}
          headline={headline}
        />
      );
    }
    if (investorSharedVehicle) {
      return <InvestorSharedVehicle vtpData={vtpData} pbcRef={pbcRef} headline={headline} />;
    }
    if (nationWideSelling) {
      return <NationwideSellingVehicle vtpData={vtpData} pbcRef={pbcRef} headline={headline} />;
    }
    return '';
  })();

  if (unitTest) {
    partner.partnerId = 'DEUA26603';
  }

  return (
    <AddressContainer showTitle={showTitle}>
      {showTitle && (
        <Headline
          tag={headlineTag}
          variant={{ type: 'headline', order: headlineOrder, weight: 'normal' }}
          data-testid="pbc-vtp-title"
          className={className()}
        >
          {vtpTitle}
        </Headline>
      )}
      {isDealerInfoIntegrated && vehicleCard}
      {!isDealerInfoIntegrated && partner && partner.partnerId !== '' && (
        <>
          <Headline
            tag={nameAs}
            variant={nameVariant}
            data-testid="pbc-partner-name"
            className="pbc-partner-name"
          >
            {isLinkedToPWS && partner.url && !vtpData?.imprint ? (
              <StyledHeadlineAsLink>
                <a
                  data-testid="pbc-partner-name-link"
                  href={partner.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={dealerNameLinkAriaLabel}
                >
                  {vtpDealerObj?.name || partnerName}
                </a>
              </StyledHeadlineAsLink>
            ) : (
              vtpDealerObj?.name || partnerName
            )}
          </Headline>

          <Wrapper>
            {vtpData?.registrationNumber && (
              <RegNumber variant={{ type: 'copy', order: '2', style: 'normal' }}>
                {registrationNumber} {vtpData?.registrationNumber}
              </RegNumber>
            )}
            {isGoogleMapLink && (
              <StyledTextLink
                variant="standalone"
                icon="geofence"
                iconPosition="leading"
                size="md"
                margin={['var(--spacing-relative-sm)', 0, 'var(--spacing-relative-lg)', 0]}
                href={dealerDirectionUrl || partner.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackingManager.click(
                    'exit_link',
                    dealerDirectionUrl || partner.directionsUrl,
                    'link',
                    dealerAddress || `${partner.address[0]} ${partner.address[1]}`,
                    partnerName,
                    '1',
                    'location',
                  );
                }}
                data-testid="pbc-vtp-directions"
              >
                {dealerAddress || partnerAddress}
              </StyledTextLink>
            )}
            {vtpData?.imprint && (
              <>
                <StyledTextLink
                  variant="standalone"
                  icon="address-book"
                  iconPosition="leading"
                  size="md"
                  onClick={() => {
                    setIsOpen(!openDialog);
                  }}
                  data-testid="pbc-imprint-trigger"
                >
                  {imprintMessage}
                </StyledTextLink>

                <ImprintModal
                  imprintRef={pbcRef}
                  imprintData={vtpData?.imprint}
                  open={openDialog}
                  close={() => {
                    setIsOpen(!openDialog);
                  }}
                />
              </>
            )}
          </Wrapper>
        </>
      )}
    </AddressContainer>
  );
};
